import { useMemo, useRef, useState, JSX } from 'react';
import { Item, RecommendationData } from '../data/recommendationDataTypes.ts';
import { useInfiniteFetchMoreData } from '../data/recommendationDataHooks.ts';
import PulseTracking from '../tracking/pulseTracking.tsx';
import { usePrefetchRecommendationDataQuery } from '../data/recommendationData.ts';
import { useBottomReached } from '../tracking/bottomReachedTracking.ts';
import { Button } from '@warp-ds/react/components/button';
import { getTitle, showHistoryLink, showLoadMoreButton } from './adHelpers.ts';
import { insertOikotieAd } from './OikotieAd.tsx';
import AdvertisingAd, { itemIsAdvertising } from './AdvertisingAd.tsx';
import { useAppConfig } from '../utils/AppConfig.tsx';
import Ad from './Ad.tsx';

type AdsProps = {
    position: string;
    recommender: string;
    adId: number;
    numColumns: number;
    rowsToRender: number;
    enableLoadMore: boolean;
};

const getComponentByItem = (
    item: Item,
    isFetched: boolean,
    tracking: PulseTracking,
) => {
    if (itemIsAdvertising(item)) {
        return (
            <AdvertisingAd
                key={item.itemId}
                item={item}
                tracking={tracking}
                inscreenTrackingIsEnabled={isFetched}
            />
        );
    }
    return (
        <Ad
            key={item.itemId}
            item={item}
            tracking={tracking}
            inscreenTrackingIsEnabled={isFetched}
        />
    );
};

export default function Ads(props: AdsProps) {
    const {
        position,
        recommender,
        adId,
        numColumns,
        rowsToRender,
        enableLoadMore,
    } = props;

    const { urls, brand, overrideTitle } = useAppConfig();
    const { apiUrl } = urls;

    const [renderAllRows, setRenderAllRows] = useState(() => {
        if (enableLoadMore) {
            return false;
        }
        return rowsToRender === 0 || !rowsToRender;
    });

    const nodeRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const loadMoreButtonRef = useRef<HTMLButtonElement>(null);

    const { recommendationData: initialData, isFetched: initialIsFetched } =
        usePrefetchRecommendationDataQuery();

    const fetchMoreUrlsPaged = renderAllRows
        ? initialData?.fetchMore
        : (initialData?.fetchMore || []).slice(
              0,
              enableLoadMore ? 0 : rowsToRender,
          );

    const { currentFetchMoreData, isFetched, hasMoreFetchData } =
        useInfiniteFetchMoreData({
            apiUrl,
            ref: nodeRef,
            recommender,
            position,
            fetchMoreUrls: fetchMoreUrlsPaged,
        });

    const tracking = useMemo(
        () =>
            new PulseTracking({
                position,
                trackingContext: initialData?.['tracking-context'] ?? {},
            }),
        [position, initialData],
    );

    const elements = useMemo(
        () =>
            (initialData?.items ?? []).map((item) =>
                getComponentByItem(item, initialIsFetched, tracking),
            ),
        [initialData, initialIsFetched, tracking],
    );

    const fetchMoreElements = useMemo(
        () =>
            currentFetchMoreData?.pages?.reduce(
                (acc, page: RecommendationData) => {
                    const items = page?.items;
                    if (items) {
                        acc.push(
                            ...items.map((item) =>
                                getComponentByItem(item, isFetched, tracking),
                            ),
                        );
                    }
                    return acc;
                },
                [] as JSX.Element[],
            ),
        [currentFetchMoreData, isFetched, tracking],
    );

    const allElements = useMemo(
        () => [...elements, ...fetchMoreElements],
        [elements, fetchMoreElements],
    );

    const isFrontpage = position?.includes('front');
    const isPersonal = initialData?.isPersonal;
    const initialDataSize = initialData?.size;

    //show Oikotie only on tori
    let adItems = allElements;
    if (brand.toUpperCase() === 'TORI' && isFrontpage) {
        adItems = insertOikotieAd(allElements, iframeRef);
    }

    const adsTitle = useMemo(() => {
        return initialIsFetched
            ? getTitle(adId, isPersonal, initialDataSize, overrideTitle)
            : '';
    }, [initialIsFetched, adId, isPersonal, initialDataSize, overrideTitle]);

    const { bottomRef, isBottomReached } = useBottomReached({
        onBottomReachedFn: () => {
            if (!hasMoreFetchData) {
                fetch(`${apiUrl}/tracking`);
            }
        },
    });

    return (
        <div>
            <div
                className="grid f-grid grid-flow-row-dense my-16"
                style={{ alignItems: 'center', display: 'flex' }}
            >
                <h1 className="text-ml">{adsTitle}</h1>
                {showHistoryLink(
                    initialDataSize,
                    brand,
                    isFrontpage,
                    isPersonal,
                ) && (
                    <a
                        href="/anbefalinger/historikk"
                        className="link text-14 px-8"
                    >
                        Hvorfor anbefaler vi disse annonsene?
                    </a>
                )}
            </div>
            <div
                className={`grid f-grid grid-flow-row-dense grid-cols-2 gap-16 ${
                    numColumns === 1
                        ? 'lg:grid-cols-1'
                        : numColumns === 5
                          ? 'lg:grid-cols-5'
                          : 'lg:grid-cols-3'
                }`}
            >
                {adItems}
                <div ref={nodeRef} />
                <div ref={bottomRef} style={{ height: '1px' }}></div>
            </div>
            <div className="text-center my-16">
                {showLoadMoreButton(
                    renderAllRows,
                    isBottomReached,
                    hasMoreFetchData,
                    initialDataSize,
                ) && (
                    <Button
                        primary
                        className="leading-m"
                        onClick={() => {
                            setRenderAllRows(true);
                        }}
                        ref={loadMoreButtonRef}
                    >
                        {'Se flere anbefalinger'}
                    </Button>
                )}
            </div>
        </div>
    );
}
